import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  Communications,
  PostcardOrder,
  ScheduledEmail,
  ScheduledNotification,
} from '../../types/communication';

export interface CommunicationsState {
  communications: Record<number, Communications>;
  templatesLoading: boolean;
}
const initialState: CommunicationsState = {
  communications: {} as Record<number, Communications>,
  templatesLoading: false,
};

const createEmptyCommunications = (): Communications => ({
  emails: [] as ScheduledEmail[],
  notifications: [] as ScheduledNotification[],
  postcards: [] as PostcardOrder[],
});

const communicationsSlice = createSlice({
  name: 'communications',
  initialState,
  reducers: {
    setTemplatesLoading: (state, action: PayloadAction<boolean>): void => {
      state.templatesLoading = action.payload;
    },
    setEmails: (
      state,
      action: PayloadAction<{ communityId: number; emails: ScheduledEmail[] }>,
    ): void => {
      const { communityId, emails } = action.payload;
      if (!state.communications[communityId]) state.communications[communityId] = createEmptyCommunications();

      state.communications[communityId].emails = emails;
    },
    setNotifications: (
      state,
      action: PayloadAction<{ communityId: number; notifications: ScheduledNotification[] }>,
    ): void => {
      const { communityId, notifications } = action.payload;
      if (!state.communications[communityId]) state.communications[communityId] = createEmptyCommunications();

      state.communications[communityId].notifications = notifications;
    },
    setPostcards: (
      state,
      action: PayloadAction<{ communityId: number; postcards: PostcardOrder[] }>,
    ): void => {
      const { communityId, postcards } = action.payload;
      if (!state.communications[communityId]) state.communications[communityId] = createEmptyCommunications();

      state.communications[communityId].postcards = postcards;
    },
    deleteEmail: (state, action: PayloadAction<{ communityId: number; emailId: number }>): void => {
      const { communityId, emailId } = action.payload;
      if (state.communications[communityId])
        state.communications[communityId].emails = state.communications[communityId].emails.filter(
          (e) => e.emailId !== emailId,
        );
    },
    deleteNotification: (
      state,
      action: PayloadAction<{ communityId: number; notificationId: number }>,
    ): void => {
      const { communityId, notificationId } = action.payload;
      if (state.communications[communityId])
        state.communications[communityId].notifications = state.communications[
          communityId
        ].notifications.filter((n) => n.notificationId !== notificationId);
    },

    updateEmail: (
      state,
      action: PayloadAction<{ communityId: number; email: ScheduledEmail }>,
    ): void => {
      const { communityId, email } = action.payload;
      if (!state.communications[communityId]) state.communications[communityId] = createEmptyCommunications();
      state.communications[communityId].emails = state.communications[communityId].emails.map((e) =>
        e.emailId === email.emailId ? email : e,
      );
      if (!state.communications[communityId].emails.find((e) => e.emailId === email.emailId))
        state.communications[communityId].emails.push(email);
    },
    updateNotification: (
      state,
      action: PayloadAction<{ communityId: number; notification: ScheduledNotification }>,
    ): void => {
      const { communityId, notification } = action.payload;
      if (!state.communications[communityId]) state.communications[communityId] = createEmptyCommunications();
      state.communications[communityId].notifications = state.communications[
        communityId
      ].notifications.map((n) =>
        n.notificationId === notification.notificationId ? notification : n,
      );
      if (
        !state.communications[communityId].notifications.find(
          (n) => n.notificationId === notification.notificationId,
        )
      )
        state.communications[communityId].notifications.push(notification);
    },
    updatePostcard: (
      state,
      action: PayloadAction<{ communityId: number; postcard: PostcardOrder }>,
    ): void => {
      const { communityId, postcard } = action.payload;
      if (!state.communications[communityId]) state.communications[communityId] = createEmptyCommunications();
      state.communications[communityId].postcards = state.communications[communityId].postcards.map(
        (p) => (p.orderId === postcard.orderId ? postcard : p),
      );
      if (!state.communications[communityId].postcards.find((p) => p.orderId === postcard.orderId))
        state.communications[communityId].postcards.push(postcard);
    },
  },
});

const communicationsReducer = communicationsSlice.reducer;

export const {
  setEmails,
  setNotifications,
  deleteEmail,
  deleteNotification,
  updateEmail,
  updateNotification,
  setPostcards,
  updatePostcard,
  setTemplatesLoading,
} = communicationsSlice.actions;
export default communicationsReducer;
